import { ApolloError, useMutation } from '@apollo/client';
import { useBreakpoint } from '@moda/om';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Gender } from '../../generated/types';
import { reportError } from '../../lib/reportError';
import { useEmailVerification } from '../useEmailVerification';
import { usePhoneNumberValidation } from '../usePhoneNumberValidation';
import { useSessionQueryParams } from '../useSessionQueryParams';
import { useEmailSignupPhoneNumber } from '../../components/EmailSignup/useEmailSignup/useEmailSignupPhoneNumber';
import { trackEmailSignup } from './trackEmailSignup';
import {
  EmailSubscribeMutation,
  EmailSubscribeMutationVariables,
  EMAIL_SUBSCRIBE_MUTATION,
  SignUpSource,
  Placement
} from './useSubscribe.rest';

export type SubscribeArgs = {
  email: string;
  label: Placement;
  phone?: string;
  source?: SignUpSource;
};

export type UseSubscribeOptions = {
  withTracking?: boolean; // fire tc event
  withUtm?: boolean; // attach utm params to tc event
  validatePhoneOnlyWhenEnabled?: boolean;
  friendlyError?: boolean; // convert gql error to UX string
};

const defaultOptions: Required<UseSubscribeOptions> = {
  withTracking: true,
  withUtm: true,
  validatePhoneOnlyWhenEnabled: true,
  friendlyError: false
};

export const useSubscribe = (opts: UseSubscribeOptions = {}) => {
  const options = useMemo(() => ({ ...defaultOptions, ...opts }), [opts]);

  const { vertical = 'women' } = useParams<{
    vertical: Gender | undefined;
    trunkshowSlug?: string;
    designerSlug?: string;
  }>();
  const isMobile = useBreakpoint({ lt: 'md' }).matches;
  const defaultSource: SignUpSource = isMobile ? 'mobile' : 'desktop';

  const { enabled: phoneNumberEnabled, phone, setPhone } = useEmailSignupPhoneNumber();

  const { isValid: isEmailValid, error: mailValidationError } = useEmailVerification();
  const { isValid: isPhoneNumberValid, error: phoneNumberValidationError } =
    usePhoneNumberValidation();

  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params: { utm_source, utm_medium, utm_campaign, utm_content, utm_term }
  } = useSessionQueryParams();

  const [mutate, { loading, error: apolloError, data }] = useMutation<
    EmailSubscribeMutation,
    EmailSubscribeMutationVariables
  >(EMAIL_SUBSCRIBE_MUTATION);

  const [isSubscribed, setIsSubscribed] = useState(false);
  const isExistingSubscription = data?.emailSubscribe.table?.isExistingSubscription ?? false;

  const normalisePhone = useCallback((rawPhone?: string) => {
    if (!rawPhone) return '';
    return `+1${rawPhone.replace(/[^0-9]/g, '')}`;
  }, []);

  const subscribe = useCallback(
    ({ email, label, phone = '', source = defaultSource }: SubscribeArgs) => {
      if (!isEmailValid(email)) return;

      const mustValidatePhone = options.validatePhoneOnlyWhenEnabled ? phoneNumberEnabled : true;

      if (mustValidatePhone && phone && !isPhoneNumberValid(phone)) return;

      if (options.withTracking) {
        trackEmailSignup({
          event: 'Email Signup Submitted',
          label,
          category: 'Marketing Signup',
          phoneProvided: Boolean(phone),
          ...(options.withUtm
            ? { utm_source, utm_medium, utm_campaign, utm_content, utm_term }
            : {})
        });
      }

      mutate({
        variables: {
          input: {
            user: {
              email,
              gender: Gender[vertical],
              phone: normalisePhone(phone)
            },
            label,
            category: 'Marketing Signup',
            sign_up_source_target: source
          }
        }
      })
        .then(() => setIsSubscribed(true))
        .catch((err: ApolloError) => reportError('EMAIL_SUBSCRIBE', err));
    },
    [
      isEmailValid,
      isPhoneNumberValid,
      mutate,
      normalisePhone,
      defaultSource,
      phoneNumberEnabled,
      options,
      utm_campaign,
      utm_content,
      utm_medium,
      utm_source,
      utm_term,
      vertical
    ]
  );

  const userFriendlyError =
    options.friendlyError && apolloError ? 'Unable to subscribe you at this time' : null;

  return {
    /* imperative */
    subscribe,

    /* mutation meta */
    loading,
    error: apolloError,
    userFriendlyError,

    /* status */
    isSubscribed,
    isExistingSubscription,

    /* input helpers */
    phoneNumberEnabled,
    phone,
    setPhone,
    isEmailValid,
    isPhoneNumberValid,
    mailValidationError,
    phoneNumberValidationError
  };
};
